import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashboardShell from "@/components/DashboardShell";
import ProjectCard, { formatPrice } from "@/components/ProjectCard";
import BillingCard from "@/components/BillingCard";
import UpcomingSiteVisits from "@/components/UpcomingSiteVisits";
import { TrendingUp, Building2, MessageSquare, Users, ArrowRight, Calendar as CalendarIcon } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [listings, setListings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [visits, setVisits] = useState([]);
  const [insight, setInsight] = useState(null);

  useEffect(() => {
    if (!user?.micro_market) return;
    (async () => {
      const [p, l, ld, sv, mi] = await Promise.all([
        api.get(`/master-projects`, { params: { zone: user.micro_market } }),
        api.get(`/listings`),
        api.get(`/leads`),
        api.get(`/site-visits`, { params: { upcoming_only: true } }).catch(() => ({ data: { rows: [] } })),
        user.primary_locality ? api.get(`/market-insights`, { params: { locality: user.primary_locality } }) : Promise.resolve({ data: null }),
      ]);
      setProjects(p.data); setListings(l.data); setLeads(ld.data);
      setVisits(sv.data?.rows || []);
      setInsight(mi.data ? Object.values(mi.data)[0] : null);
    })();
  }, [user]);

  return (
    <DashboardShell>
      <div className="overline text-sg-gold-deep">YOUR CONTROL ROOM</div>
      <h1 className="font-display text-4xl lg:text-5xl text-sg-navy mt-2">
        Hello, <span className="font-serif-italic text-sg-gold-deep">{user?.name?.split(" ")[0]}.</span>
      </h1>
      <p className="text-sg-mute mt-2">
        Operating · <span className="font-medium text-sg-navy">{user?.primary_locality}, {user?.micro_market}</span>
      </p>

      {insight && (
        <div className="mt-8 sg-card-cream flex items-start gap-4" data-testid="market-insight-card">
          <div className="w-12 h-12 rounded-full bg-white border border-sg-line flex items-center justify-center"><TrendingUp className="w-5 h-5 text-sg-gold-deep" /></div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="overline text-sg-gold-deep">MARKET INSIGHT · {user?.primary_locality?.toUpperCase()}</span>
              <span className="rupee text-2xl text-sg-navy">{insight.yoy}<span className="text-xs text-sg-mute ml-1 font-sans">YoY</span></span>
            </div>
            <p className="text-sm mt-1 text-sg-navy">{insight.summary}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
        <StatCard icon={<Building2 className="w-5 h-5" />} label="Master Projects in your corridor" value={projects.length} to="/dashboard/projects" testid="stat-master-projects" />
        <StatCard icon={<MessageSquare className="w-5 h-5" />} label="Your listings" value={listings.length} to="/dashboard/listings" testid="stat-listings" />
        <StatCard icon={<Users className="w-5 h-5" />} label="Active leads" value={leads.length} to="/dashboard/leads" testid="stat-leads" />
        <StatCard icon={<CalendarIcon className="w-5 h-5" />} label="Upcoming site visits" value={visits.length} to="/dashboard/leads" testid="stat-site-visits" />
      </div>

      {visits.length > 0 && (
        <div className="mt-8">
          <UpcomingSiteVisits visits={visits} onStatusChange={(id, status) => {
            setVisits((vs) => vs.map((v) => v.visit_id === id ? { ...v, status } : v).filter((v) => v.status !== "cancelled"));
          }} />
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3 mt-12 mb-6">
        <h2 className="font-display text-3xl lg:text-4xl text-sg-navy">
          Featured launches · <span className="font-serif-italic text-sg-gold-deep">{user?.micro_market}</span>
        </h2>
        <Link to="/dashboard/projects" className="pill-outline inline-flex items-center gap-2" data-testid="see-all-projects">
          See all <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.slice(0, 6).map((p) => (
          <ProjectCard key={p.project_id} project={p} tag="NEW LAUNCH" testIdPrefix="dashboard-project" />
        ))}
      </div>

      <div className="mt-12 max-w-2xl">
        <BillingCard compact />
      </div>
    </DashboardShell>
  );
}

function StatCard({ icon, label, value, to, testid }) {
  return (
    <Link to={to} data-testid={testid} className="sg-card p-6 block">
      <div className="flex items-center gap-3 text-sg-mute">
        <div className="w-9 h-9 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center text-sg-gold-deep">{icon}</div>
        <span className="text-sm">{label}</span>
      </div>
      <div className="font-display text-4xl mt-3 text-sg-navy">{value}</div>
    </Link>
  );
}
